import { ChangeDetectorRef, Component, OnDestroy, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import * as fromStore from '@app/modules/admission/_store';
import { AuthService } from '@app/core/_guards/auth/auth.service';
import { GoogleAnalyticsEventsService } from '@app/core/_shared/services/google-analytics/google-analytics-events.service';
import * as fromServices from '@app/core/_shared/services/organizations/organizations.service';
import { getCurrentUser, setCurrentAccount } from '@app/core/_shared/services/storage/storage.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() set menu(open: boolean) {
    this.isOpen = open;
  }
  @Output() notify = new EventEmitter<boolean>();

  isLoggedIn: any;
  isOpen: boolean;
  currentAccount: any;
  user: any;
  permissions: any;
  baseUrl = '/';
  account: string;
  canShowSearch = false;

  private readonly searchRoutes = ['/', '/candidate', '/search'] as const;
  private lastUrl: string;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private store: Store<any>,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private eventGa: GoogleAnalyticsEventsService,
    private organizationService: fromServices.OrganizationsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.checkUrl();

    this.user = getCurrentUser();
    this.isLoggedIn = this.authService.loggedIn();

    this.initializeAccount();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeAccount(): void {
    this.activatedRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params: Params) => {
      this.account = params.a;
      this.router.navigate([], {
        queryParams: { a: null },
        queryParamsHandling: 'merge'
      });

      if (this.account) {
        setCurrentAccount(this.account);
        this.currentAccount = this.organizationService.setOrganization(this.account);
      }

      if (!this.currentAccount) {
        this.store.pipe(select(fromStore.getOrganizationState), takeUntil(this.destroy$)).subscribe(res => {
          this.currentAccount = res.currentAccount;
        });
      }
      this.eventGa.customPropreties('dimension1', this.currentAccount.name);
    });
  }

  checkUrl(): void {
    this.baseUrl = '/';
    this.lastUrl = this.router.url;
    this.canShowSearch = this.canDisplaySearch();
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateSearchVisibility());
  }

  private updateSearchVisibility(): void {
    if (this.router.url === this.lastUrl) {
      return;
    }
    this.lastUrl = this.router.url;
    this.canShowSearch = this.canDisplaySearch();
    this.cdr.detectChanges();
  }

  private canDisplaySearch(): boolean {
    const path = this.router.url.split(/[?#;]/)[0].replace(/\/+$/, '') || '/';
    return this.searchRoutes.some(route => path === route || path.startsWith(`${route}/`));
  }

  openMenu(): void {
    const body = document.getElementsByTagName('body')[0];
    this.isOpen = !this.isOpen;
    this.notify.emit(this.isOpen);
    body.classList.toggle('sidebar-is-open', this.isOpen);
  }

  preferences(): void {
    this.router.navigate(['/preferences']);
  }

  logout(): void {
    this.authService.logout();
  }
}
